import React, { useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useSelector } from 'react-redux';
import { OptionsType } from 'react-select';
import { UseFiltersColumnProps } from 'react-table';
import { ParticipantTag, selectCurrentPanel } from 'store';

import { Theme } from '@app/theme';
import { CreatableTagsAutocomplete } from '@app/form';
import { DeselectCheckbox } from './DeselectCheckbox';
import { IsEmptyCheckboxFilter } from './IsEmptyCheckboxFilter';

import { useDebouncedSetFilter } from './useDebounce';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase,
  },
  input: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  column: UseFiltersColumnProps<any>;
}

export const TagsFilter = ({ column: { setFilter, filterValue } }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const panel = useSelector(selectCurrentPanel);
  const tags = panel && panel.tags ? panel.tags : [];

  const [includeFilteredTags, setIncludeFilteredTags] = useState<
    OptionsType<ParticipantTag>
  >(filterValue ? filterValue.include : []);
  const [excludeFilteredTags, setExcludeFilteredTags] = useState<
    OptionsType<ParticipantTag>
  >(filterValue ? filterValue.exclude : []);

  const debouncedSetFilter = useDebouncedSetFilter(setFilter);

  const handleIncludeChange = useCallback(
    (option: OptionsType<any>) => {
      setIncludeFilteredTags(option);
      if (!option.length && !excludeFilteredTags.length) {
        debouncedSetFilter(undefined);
      } else {
        debouncedSetFilter({
          include: option,
          exclude: excludeFilteredTags,
        });
      }
    },
    [debouncedSetFilter, excludeFilteredTags],
  );

  const handleExcludeChange = useCallback(
    (option: OptionsType<any>) => {
      setExcludeFilteredTags(option);
      if (!option.length && !includeFilteredTags.length) {
        debouncedSetFilter(undefined);
      } else {
        debouncedSetFilter({
          include: includeFilteredTags,
          exclude: option,
        });
      }
    },
    [debouncedSetFilter, includeFilteredTags],
  );

  const selectNone = useCallback(() => {
    debouncedSetFilter(undefined);
  }, [debouncedSetFilter]);
  const selectAll = useCallback(() => {
    debouncedSetFilter({ include: tags });
  }, [debouncedSetFilter, tags]);

  return (
    <div className={classes.container}>
      <CreatableTagsAutocomplete
        label={t('participants.list.filters.include')}
        noMargin
        isMulti
        onChange={handleIncludeChange}
        value={includeFilteredTags}
        options={tags}
        creatable={false}
        containerClass={classes.input}
      />
      <CreatableTagsAutocomplete
        label={t('participants.list.filters.exclude')}
        noMargin
        isMulti
        onChange={handleExcludeChange}
        value={excludeFilteredTags}
        options={tags}
        creatable={false}
        containerClass={classes.input}
      />
      <DeselectCheckbox
        selectAll={selectAll}
        selectNone={selectNone}
        setValue={setIncludeFilteredTags}
        filterValue={includeFilteredTags}
        allValue={tags}
      />
      <IsEmptyCheckboxFilter
        debouncedSetFilter={debouncedSetFilter}
        setValue={setIncludeFilteredTags}
        value={includeFilteredTags}
      />
    </div>
  );
};
